/**
 * Permission manager for Cairn
 * Handles checking and opening settings for all app permissions.
 * Clicking any permission always opens the native settings page.
 */

import { logger } from '../../utils/logger.js'
import { Capacitor } from '@capacitor/core'

const log = logger.create('permissions')

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unavailable'

export interface PermissionStatus {
  location: PermissionState
  /** Whether fine (precise) location is granted vs only coarse */
  fineLocation: boolean
  /** Whether background location ("Allow all the time") is granted */
  backgroundLocation: boolean
  storage: PermissionState
  /** Activity recognition permission (Android 10+) — needed for auto-detecting hike/run/bike */
  activityRecognition: PermissionState
}

export class PermissionManager {
  private static instance: PermissionManager | undefined
  private permissionStatus: PermissionStatus = {
    location: 'prompt',
    fineLocation: false,
    backgroundLocation: false,
    storage: 'prompt',
    activityRecognition: 'prompt',
  }

  private constructor() {}

  static getInstance(): PermissionManager {
    PermissionManager.instance ??= new PermissionManager()
    return PermissionManager.instance
  }

  needsLocationDisclosure(): boolean {
    if (!Capacitor.isNativePlatform()) return false
    try {
      return !localStorage.getItem('cairn_location_disclosure_shown')
    } catch {
      return true
    }
  }

  markDisclosureShown(): void {
    try {
      localStorage.setItem('cairn_location_disclosure_shown', new Date().toISOString())
    } catch {
      // localStorage unavailable
    }
  }

  /**
   * Request ALL permissions upfront before any plugin registration.
   */
  async requestAllPermissions(): Promise<PermissionStatus> {
    log.info('🔐 Requesting ALL permissions upfront...')

    if (!Capacitor.isNativePlatform()) {
      log.info('🔐 Web platform - no native permissions needed')
      return this.permissionStatus
    }

    try {
      const { CairnCompass } = await import('cairn-compass')
      await CairnCompass.requestLocationPermissions()
      this.permissionStatus.location = 'granted'
    } catch (error) {
      log.error('🔐 Location permission failed:', error)
      this.permissionStatus.location = 'denied'
    }

    try {
      const { Filesystem } = await import('@capacitor/filesystem')
      const permissions = await Filesystem.requestPermissions()
      this.permissionStatus.storage = permissions.publicStorage === 'granted' ? 'granted' : 'denied'
    } catch (error) {
      log.error('🔐 Storage permission failed:', error)
      this.permissionStatus.storage = 'denied'
    }

    log.info('🔐 All permissions requested:', this.permissionStatus)
    return this.permissionStatus
  }

  /**
   * Check current permission statuses from the OS.
   */
  async checkAllPermissions(): Promise<PermissionStatus> {
    if (!Capacitor.isNativePlatform()) {
      return this.checkWebPermissions()
    }
    return this.checkNativePermissions()
  }

  private async checkWebPermissions(): Promise<PermissionStatus> {
    // Location
    if ('geolocation' in navigator) {
      try {
        const loc = await navigator.permissions.query({ name: 'geolocation' })
        this.permissionStatus.location = loc.state as PermissionState
        this.permissionStatus.fineLocation = loc.state === 'granted'
        this.permissionStatus.backgroundLocation = false
      } catch {
        this.permissionStatus.location = 'prompt'
        this.permissionStatus.fineLocation = false
      }
    } else {
      this.permissionStatus.location = 'unavailable'
      this.permissionStatus.fineLocation = false
    }

    this.permissionStatus.storage = 'unavailable'
    this.permissionStatus.backgroundLocation = false
    this.permissionStatus.activityRecognition = 'unavailable'
    return { ...this.permissionStatus }
  }

  private async checkNativePermissions(): Promise<PermissionStatus> {
    // Location — use CairnCompass for full fine/coarse/background check
    try {
      const { CairnCompass } = await import('cairn-compass')
      const locResult = await CairnCompass.checkLocationPermission()
      this.permissionStatus.location = locResult.granted ? 'granted' : 'denied'
      this.permissionStatus.fineLocation = locResult.fineLocation
      this.permissionStatus.backgroundLocation = locResult.backgroundLocation
    } catch {
      this.permissionStatus.location = 'denied'
      this.permissionStatus.fineLocation = false
      this.permissionStatus.backgroundLocation = false
    }

    // Storage
    try {
      const { Filesystem } = await import('@capacitor/filesystem')
      const fsResult = await Filesystem.checkPermissions()
      this.permissionStatus.storage = fsResult.publicStorage as PermissionState
    } catch {
      this.permissionStatus.storage = 'denied'
    }

    // Activity Recognition (Android 10+)
    try {
      const { CairnCompass } = await import('cairn-compass')
      const arResult = await CairnCompass.checkActivityRecognitionPermission()
      this.permissionStatus.activityRecognition = arResult.granted ? 'granted' : 'denied'
    } catch {
      this.permissionStatus.activityRecognition = 'unavailable'
    }

    return { ...this.permissionStatus }
  }

  /**
   * Open the native app settings page using capacitor-native-settings.
   */
  async openNativeSettings(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return
    }
    try {
      const { NativeSettings, AndroidSettings, IOSSettings } =
        await import('capacitor-native-settings')
      if (Capacitor.getPlatform() === 'android') {
        await NativeSettings.openAndroid({ option: AndroidSettings.ApplicationDetails })
      } else {
        await NativeSettings.openIOS({ option: IOSSettings.App })
      }
    } catch (error) {
      log.error('Failed to open native settings:', error)
    }
  }

  /**
   * Request a single permission. On native, opens settings.
   * On web, triggers the browser permission prompt.
   */
  async requestPermission(key: keyof PermissionStatus): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      // On native, always open settings — the user can set what they want there
      await this.openNativeSettings()
      return
    }

    // Web-specific prompts
    switch (key) {
      case 'location':
        try {
          await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
          })
          this.permissionStatus.location = 'granted'
          this.permissionStatus.fineLocation = true
        } catch {
          this.permissionStatus.location = 'denied'
        }
        break
    }
  }

  /**
   * Check if critical permissions are granted
   */
  hasCriticalPermissions(): boolean {
    return this.permissionStatus.location === 'granted'
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): PermissionStatus {
    return { ...this.permissionStatus }
  }
}

// Export singleton instance
export const permissionManager = PermissionManager.getInstance()
